import { sdk } from '@/lib/sdk'
import type { EmailNoticeResult, RecipientCountQuery } from '@/lib/sdk-official/resources/email-notices'

/**
 * F-104: the "Avisar por e-mail" switch of the publish forms and the toast
 * that tells the team what the publication did with the notice.
 */

/** Whether the switch shows, and how many people the notice would reach now. */
export interface NoticeAudience {
  /** False for whoever is not on the team (403) and for an item that already had its notice. */
  available: boolean
  /** Null when the count could not be read: the switch still shows, without a number. */
  count: number | null
}

const statusOf = (error: unknown): number | undefined => {
  const e = error as { status?: number; response?: { status?: number } } | null
  return e?.response?.status ?? e?.status
}

/** Asks the API; the team-only rule and the "already notified" mark are the server's, not recomputed here. */
export async function fetchNoticeAudience(query: RecipientCountQuery): Promise<NoticeAudience> {
  try {
    const r = await sdk.emailNotices.recipientCount(query)
    if (r?.alreadyNotified) return { available: false, count: null }
    return { available: true, count: typeof r?.recipientCount === 'number' ? r.recipientCount : null }
  } catch (error) {
    const status = statusOf(error)
    if (status === 401 || status === 403 || status === 404) return { available: false, count: null }
    return { available: true, count: null }
  }
}

const EVENT = 'memberfy:email-notice'

/** Shows the toast for a publish response's `emailNotice` (nothing when absent). */
export function announceEmailNotice(notice: EmailNoticeResult | null | undefined): void {
  if (!notice || typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<EmailNoticeResult>(EVENT, { detail: notice }))
}

/** Subscribes to the announcements; returns the unsubscribe. */
export function onEmailNotice(listener: (notice: EmailNoticeResult) => void): () => void {
  const handler = (e: Event) => listener((e as CustomEvent<EmailNoticeResult>).detail)
  window.addEventListener(EVENT, handler)
  return () => window.removeEventListener(EVENT, handler)
}

/** Reasons worth telling the team; the others (switch off, not on the team) are what they chose or cannot change. */
export const TOLD_REASONS = ['alreadyNotified', 'backdated', 'pastEvent', 'notStoreItem', 'error'] as const

/** A date-time in the past: publishing backdated never announces, so the switch is not shown. */
export const isPast = (iso: string | null | undefined): boolean => {
  if (!iso) return false
  const at = new Date(iso).getTime()
  return Number.isFinite(at) && at < Date.now()
}
